import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useMembershipCategoryConfigurations } from '@/utils/membership'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import z from 'zod'
import { useIsSavingMembershipApplicationDraft, useMembershipApplicationDraft, useSaveMembershipApplicationDraft, type MembershipApplicationCategoryDraft } from '../api/useMembershipApplicationDraft'
import { membershipApplicationSteps } from '../constants'
import { useApplicationSectionConfiguration } from '../hooks/useApplicationConfiguration'
import { RequirementSummaryList } from './requirement-summary-list'

const categoryFormSchema = z.object({
  categoryId: z.string().min(1, "Select the membership category you are applying for."),
  prerequisitesAccepted: z.boolean().refine((value) => value === true, {
    message: "You must confirm that you meet the category prerequisites.",
  }),
})

export type CategoryValues = z.infer<typeof categoryFormSchema>

function mapDraftToFormValues(category?: MembershipApplicationCategoryDraft): CategoryValues {
  
  const categoryIdValue = category?.categoryId && category.categoryId > 0 
    ? String(category.categoryId) 
    : "";
  
  return {
    categoryId: categoryIdValue,
    prerequisitesAccepted: category?.prerequisitesAccepted ?? false,
  }
}


function mapFormValuesToDraft(values: CategoryValues): MembershipApplicationCategoryDraft {
  return {
    categoryId: Number.parseInt(values.categoryId, 10) || null,
    prerequisitesAccepted: values.prerequisitesAccepted,
  }
}

export default function CategoryStep() {

  const navigate = useNavigate()
  const { data: draft, isLoading: isDraftLoading } = useMembershipApplicationDraft()
  const saveDraftMutation = useSaveMembershipApplicationDraft()
  const isSavingDraft = useIsSavingMembershipApplicationDraft()
  const categoriesQuery = useMembershipCategoryConfigurations()
  const [isInitializing, setIsInitializing] = useState(true)
  
  const formDefaultValues = useMemo(() => mapDraftToFormValues(draft?.category), [draft?.category])

  const form = useForm<CategoryValues>({
    resolver: zodResolver(categoryFormSchema),
    defaultValues: formDefaultValues,
    mode: "onTouched",
    reValidateMode: "onChange",
  })
  

  const categoryEntries = useMemo(
    () =>
      categoriesQuery.categoriesWithConfiguration.filter(
        (entry) => entry.category.active !== false && entry.category.id != null,
      ),
    [categoriesQuery.categoriesWithConfiguration],
  )
  

  const watchedCategoryId = form.watch("categoryId")
  
  const selectedCategoryEntry = useMemo(() => {
    return categoryEntries.find((entry) => 
      String(entry.category.id) === String(watchedCategoryId)
    )
  }, [categoryEntries, watchedCategoryId])

  const selectedCategory = selectedCategoryEntry?.category
  const selectedCategoryConfiguration = selectedCategoryEntry?.configuration

  const selectedCategoryNumericId = selectedCategory?.id ?? draft?.category?.categoryId ?? null
  const { nextStep, section: categorySection } = useApplicationSectionConfiguration(
    "category",
    selectedCategoryNumericId
  )

  const stepTitleMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const step of membershipApplicationSteps) {
      map.set(step.id, step.title)
    }
    return map
  }, [])

  const { optionalSections } = useMemo(() => {
    if (!selectedCategoryConfiguration) {
      return { requiredSections: [], optionalSections: [] }
    }

    const sections = Object.values(selectedCategoryConfiguration.sections).filter(
      (section) => section.stepId !== "review" && section.isVisible,
    )

    return {
      requiredSections: sections.filter((section) => section.isRequired),
      optionalSections: sections.filter((section) => !section.isRequired),
    }
  }, [selectedCategoryConfiguration])


  const handleSubmit = useCallback(
    async (values: CategoryValues) => {
      const payload = mapFormValuesToDraft(values)
      await saveDraftMutation.mutateAsync({
        category: payload,
      })

      if (nextStep?.path) {
        navigate({ to: nextStep?.path })
      }
    },
    [navigate, nextStep?.path, saveDraftMutation],
  )

  const isContinueDisabled = saveDraftMutation.isPending || isSavingDraft
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const previousCategoryIdRef = useRef<string | undefined>(undefined)

  // Form reset effect
  useEffect(() => {
    form.reset(formDefaultValues)
  }, [formDefaultValues, form])

  // Initialize after data loads
  useEffect(() => {
    if (!isDraftLoading && !categoriesQuery.isLoading) {
      const timer = setTimeout(() => {
        setIsInitializing(false)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isDraftLoading, categoriesQuery.isLoading])

  // Category change detection
  useEffect(() => {
    const currentCategoryId = watchedCategoryId
    const previousCategoryId = previousCategoryIdRef.current

    // Skip initial render
    if (previousCategoryId === undefined) {
      previousCategoryIdRef.current = currentCategoryId
      return
    }

    // If category changed
    if (previousCategoryId !== currentCategoryId && currentCategoryId) {
      console.log('Category changed from', previousCategoryId, 'to', currentCategoryId)
      // Clear requirements and documents since they are category-specific
      saveDraftMutation.mutate({
        requirements: {},
        supportingDocuments: [],
      })
    }

    previousCategoryIdRef.current = currentCategoryId
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedCategoryId])

  // Autosave effect
  useEffect(() => {
    let skipNextAutosave = true

    const subscription = form.watch((values, info) => {
      if (skipNextAutosave) {
        skipNextAutosave = false
        return
      }

      if (info?.type !== "change" && info?.type !== "blur") {
        return
      }

      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current)
      }

      autosaveTimerRef.current = setTimeout(() => {
        const parsed = categoryFormSchema.safeParse(values)
        if (!parsed.success) {
          return
        }

        const payload = mapFormValuesToDraft(parsed.data)
        saveDraftMutation.mutate({ category: payload })
      }, 800)
    })

    return () => {
      subscription.unsubscribe()
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  // Show loading skeleton while draft is being fetched or form is initializing
  if (isDraftLoading || isInitializing || categoriesQuery.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Membership category</CardTitle>
          <CardDescription>
            Loading your application data...
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-3 w-64" />
          </div>
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    )
  }

  return <Form {...form}>
    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Membership category</CardTitle>
              <CardDescription>
                {categorySection?.guidance ?? "Choose the category that aligns with your qualifications and acknowledge its prerequisites."}
              </CardDescription>
            </div>
            {isSavingDraft && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Saving...</span>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <FormField
            control={form.control}
            name="categoryId"
            render={({ field, fieldState }) => (
              <FormItem className="space-y-2">
                <FormLabel required>Category</FormLabel>
                <FormControl>
                  <Select
                    key={field.value || 'empty'}
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={categoriesQuery.isLoading || categoryEntries.length === 0}
                  >
                    <SelectTrigger data-invalid={fieldState.invalid}>
                      <SelectValue
                        placeholder={
                          categoriesQuery.isLoading
                            ? "Loading categories..."
                            : categoryEntries.length === 0
                              ? "No categories available"
                              : "Select membership category"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {categoryEntries.map(({ category }) => (
                        <SelectItem key={category.id} value={String(category.id)}>
                          {category.categoryName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormDescription>
                  Selecting a category will display its overview and prerequisite guidance.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {categoriesQuery.isError ? (
            <Alert variant="destructive">
              <AlertTitle>Unable to load categories</AlertTitle>
              <AlertDescription>
                Please refresh the page or try again later.
              </AlertDescription>
            </Alert>
          ) : null}

          {categoriesQuery.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : selectedCategory ? (
            <Alert>
              <AlertTitle>{selectedCategory.categoryName}</AlertTitle>
              <AlertDescription>
                <div className="space-y-3 text-sm leading-6">
                  {selectedCategory.description?.trim()?.length ? (
                    <p>{selectedCategory.description}</p>
                  ) : null}
                  {selectedCategoryConfiguration ? (
                    <>
                      <p>{selectedCategoryConfiguration.summary}</p>
                      {selectedCategoryConfiguration.prerequisites.length ? (
                        <div className="space-y-1">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Prerequisites
                          </p>
                          <ul className="list-disc space-y-1 pl-5">
                            {selectedCategoryConfiguration.prerequisites.map((item) => (
                              <li key={item}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {optionalSections.length ? (
                        <div className="space-y-1">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Optional sections
                          </p>
                          <ul className="list-disc space-y-1 pl-5">
                            {optionalSections.map((section) => (
                              <li key={section.stepId}>{stepTitleMap.get(section.stepId) ?? section.stepId}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {selectedCategoryConfiguration.supportingDocuments?.length ? (
                        <div className="space-y-1">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Documents to prepare
                          </p>
                          <ul className="list-disc space-y-1 pl-5">
                            {selectedCategoryConfiguration.supportingDocuments.map((item) => (
                              <li key={item}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <p>
                      We will display the specific prerequisites for this category once provided by the
                      membership team. For now, ensure you meet the minimum qualifications before continuing.
                    </p>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          ) : null}

          <FormField
            control={form.control}
            name="prerequisitesAccepted"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormControl>
                  <div className="flex items-start gap-3 rounded-md border border-border bg-muted/20 p-4">
                    <Checkbox
                      id="prerequisitesAccepted"
                      checked={field.value}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                    />
                    <div className="space-y-1">
                      <FormLabel required htmlFor="prerequisitesAccepted" className="text-sm font-medium">
                        I meet the prerequisites for this category
                      </FormLabel>
                      <FormDescription>
                        {selectedCategoryConfiguration && selectedCategory
                          ? `Confirm that you satisfy the listed prerequisites for ${selectedCategory.categoryName}.`
                          : "Select a category to review its requirements before confirming."}
                      </FormDescription>
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {categorySection?.requirementSummaries && categorySection.requirementSummaries.length > 0 ? (
            <RequirementSummaryList
              title="Category-specific requirements"
              summaries={categorySection.requirementSummaries}
            />
          ) : null}
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button type="submit" disabled={isContinueDisabled}>
            {isContinueDisabled ? "Saving..." : "Continue to Profile"}
          </Button>
        </CardFooter>
      </Card>

    </form>
  </Form>
}
